import type { ReactElement } from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import logoRaw from '@/assets/logo-sportadmin.svg?raw';

// The wordmark ships with a hardcoded ink fill; recolour it to currentColor
// so it tracks the card's text colour (and adapts in dark mode). Computed
// once at module load.
const LOGO_HTML = logoRaw.replace(/#1a1a1a/gi, 'currentColor');

export type StatusScreenAction = {
  label: string;
  onClick: () => void;
  icon?: ReactElement | undefined;
  variant?: ButtonProps['variant'] | undefined;
};

export type StatusScreenProps = {
  // Small uppercase label above the title (optional).
  eyebrow?: string | undefined;
  title: string;
  body?: string | undefined;
  actions?: StatusScreenAction[] | undefined;
  className?: string | undefined;
};

// StatusScreen is the shared, on-brand chrome for full-area "nothing to
// show, here's why" states (access denied, load failure, …). The
// SportAdmin symbol sits in a lime badge, sentence-case heading, pill
// actions. Callers supply the copy + actions; this owns the layout so
// every status surface looks the same.
export function StatusScreen({
  eyebrow,
  title,
  body,
  actions,
  className,
}: StatusScreenProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex min-h-[60vh] w-full items-center justify-center p-4',
        className,
      )}
    >
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-3xl border border-border/60 bg-card p-8 text-center shadow-lg sm:p-10">
        <span
          className="text-foreground [&_svg]:h-auto [&_svg]:w-44"
          aria-hidden
          // eslint-disable-next-line react/no-danger -- SVG is bundled at build time, not user input
          dangerouslySetInnerHTML={{ __html: LOGO_HTML }}
        />
        {eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50">
            {eyebrow}
          </p>
        )}
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">
          {title}
        </h1>
        {body && <p className="max-w-sm text-sm text-foreground/70">{body}</p>}
        {actions && actions.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            {actions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant ?? 'default'}
                onClick={action.onClick}
              >
                {action.icon}
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
